import { TableColumnsType, Flex, Tag } from 'antd';
import { useMemo } from 'react';
import {
  BaseTableUserRecord,
  TableRow,
  UserListProps
} from '../user-table-types';
import { useResolveActionsColumnData } from './use-resolveActionsColumnData';
import { useResolveStatusColumnData } from './use-resolveStatusColumnData';

/**
 * Create the colums of the user table
 *
 * Columns
 * - Full Name
 * - Email
 * - . . . props.extraColumns
 * - Actions
 * - Status
 *
 * Invoking an action will cause it to refetch table data
 * @param props
 * @param reloadData
 * @returns
 */
export function useUserTableColumns<T extends BaseTableUserRecord>(
  props: UserListProps<T>,
  reloadData: () => void
) {
  // Resolve the actions column
  const actions = useResolveActionsColumnData(props, reloadData);

  // Resolve the status column
  const status = useResolveStatusColumnData(props);

  /**
   * The columns will only change when extraColumns changes
   */
  const columns: TableColumnsType<TableRow<T>> = useMemo(
    () => [
      // Insert the mandatory fields
      {
        title: 'Full Name',
        dataIndex: 'firstName',
        sorter: (a, b) => a.firstName.localeCompare(b.firstName),
        render: (_: unknown, r: TableRow<T>) => {
          const fullName = [r.firstName, r.lastName].join(' ');
          const isPrincipal = props.principalUser && props.principalUser.uid === r.raw.id;
          if (isPrincipal) {
            return (
              <Flex gap={8} align="center">
                <span>{fullName}</span>
                <Tag color="blue">Principal User</Tag>
              </Flex>
            );
          }
          return fullName;
        }
      },
      {
        title: 'Email Address',
        dataIndex: 'email',
        sorter: (a, b) => a.email.localeCompare(b.email)
      },
      // Insert extra columns if they exist
      ...(props.extraColumns ?? []),
      {
        title: 'Status',
        dataIndex: 'status',
        sorter: (a, b) => Number(a.invitePending) - Number(b.invitePending),
        render: status
      },
      // Insert the action column
      {
        title: 'Actions',
        dataIndex: 'actions',
        render: actions
      }
    ],
    [props.extraColumns, actions, status, props.principalUser]
  );

  return columns;
}
